import React, { useMemo } from 'react';
import { wave } from '../lib/waves';

/*
 * "Sound stops. Air doesn't." — the product claim, made without a single
 * performance figure.
 *
 * Deliberately NOT called a panel: it lets air pass while damping a specific
 * band, and "panel" implies a flat sheet that blocks — the opposite of how it
 * works.
 *
 * "Silencer" rather than "attenuator" (settled 2026-08-12). They name the same
 * thing — attenuator is the UK usage — but silencer is the word a mechanical
 * schedule carries, with a CSI section number behind it, and it matches the SLA
 * deck and the customer quote PDF. A buyer can put a silencer on a schedule;
 * nobody procures a "metamaterial".
 *
 * It also sits ALONGSIDE conventional treatment rather than replacing it:
 * acoustic walls and shells do well at higher and mid frequencies, and this
 * covers the low tonal band they can't reach. Copy here should never disparage
 * walls, louvers or shells — they are often already on the site, and being
 * complementary is the easier sale.
 *
 * The frequency band (50–125 Hz) is problem-domain description, not a
 * performance spec: it says where the difficulty lives, not how well we do
 * against it. Still no dB, no percentages, no results chart.
 *
 * ── On tonal tracking (added 2026-08-31) ───────────────────────────────────
 * The element is not a fixed-tune resonator. It is set around the site's
 * measured fan tone and then follows that tone in service as fan speed moves
 * with load and ambient. Source is the customer-facing fan-tone technical brief
 * (zenbul-activity/business-development/generic, 2026-08-27), and the site
 * follows the brief's own discipline: describe the behaviour, never the
 * mechanism. Sayable: set once, listens for the tone, re-tunes itself, nothing
 * wired to the plant, nothing swapped out, tunes independently so one off-tune
 * cartridge never takes the array with it, sits outside the airstream. NOT
 * sayable: how it tunes (no resonator type, no part names, no moving-part
 * words), how wide the tracking band is, how often it re-tunes, or any
 * insertion-loss figure — those are internal or predicted, and the brief
 * itself labels them "not a guaranteed number". Also avoid calling it
 * "active": on a website that reads as loudspeaker-based noise cancellation,
 * which it is not.
 */

const ATTRIBUTES = [
  {
    n: '01',
    title: 'Built for 50–125 Hz',
    /* Named as the SLA's own bands, not just as a spec range — this is the
       attribute that closes the loop back to the low-frequency clause. */
    body: 'The bands the low-frequency clause is written against, and the ones that carry furthest. Mass and absorption do well higher up; reaching this far down with them alone takes impractical thickness.',
  },
  {
    n: '02',
    /* The tracking attribute. A fan's blade-pass tone is blades × rpm, so it
       moves every time the fan speed does, and fan speed follows load and
       weather. A silencer tuned once is on target for an hour. This is the
       reason "matched to your site" alone was never enough, and it absorbs
       that old attribute: the site measurement sets the starting point, the
       tracking does the rest. */
    title: 'Set to your tone, then follows it',
    body: 'A fan’s tone moves with its speed, and speed moves with load and weather through the night and the seasons. We measure your fans and set the silencer around their tone; from there it listens for the tone and re-tunes itself as it drifts, with nothing wired to your plant and nothing swapped out.',
  },
  {
    n: '03',
    title: 'Air passes through',
    body: 'Nothing sits in the airstream. The bore stays clear, so treating that band stops costing you the cooling the equipment depends on.',
  },
  {
    n: '04',
    title: 'Works with what’s already there',
    body: 'Deploys alongside the screen on the solid boundaries and any acoustic walls already handling the higher bands, and can be built into shells that cover mid frequencies but struggle with the low tonal content.',
  },
];

/*
 * Schematic, not measured data.
 *
 * Rows are frequency bands, low at top. Each trace enters loud at the left and
 * damps toward flat at the right. The 50–125 Hz rows are marked because that is
 * the hard part and the reason the silencer exists — not because the rest is
 * untreated.
 */
const BANDS = [
  { label: '50', freq: 2.0 },
  { label: '63', freq: 2.8 },
  { label: '80', freq: 3.8 },
  { label: '100', freq: 5.0 },
  { label: '125', freq: 6.4 },
  { label: '250', freq: 9.5 },
  { label: '500', freq: 13.5 },
  { label: '1k', freq: 18.0 },
];

const BAND_END = 4; // last row inside 50–125 Hz
const TRACE_X = 100;
const TRACE_W = 482;
const ROW_Y = (i) => 56 + i * 40;
/* Where the silencer sits. wave()'s decay holds amplitude early then
   collapses, so putting it at ~40% makes the loud/quiet split land on this
   line. */
const SILENCER_X = TRACE_X + TRACE_W * 0.4;

function Cutaway() {
  const rows = useMemo(
    () =>
      BANDS.map((b, i) => ({
        ...b,
        inBand: i <= BAND_END,
        d: wave(TRACE_W, 0, i <= BAND_END ? 11 : 7, b.freq, i * 0.8, 0.03, {
          start: 0.4, // matches SILENCER_X — loud up to it, damped after
          // Fast collapse just past it, then a long quiet run. A slow taper
          // would make the silencer look like it barely does anything.
          ease: true,
          exp: 6,
        }),
      })),
    []
  );

  const bracketTop = ROW_Y(0) - 15;
  const bracketBottom = ROW_Y(BAND_END) + 15;

  return (
    <figure className="silencer-figure">
      <svg
        viewBox="0 0 600 440"
        className="silencer-figure-svg"
        role="img"
        aria-label="Schematic: noise in eight frequency bands, 50 hertz to 1 kilohertz, arrives loud at the left, passes through the silencer, and leaves damped at the right. The 50 to 125 hertz bands are marked as the ones conventional treatment cannot reach."
      >
        {/* Band caption sits in the plot area — beside the bracket it
            collided with the frequency labels. */}
        <text className="silencer-figure-axis silencer-figure-axis--accent" x={TRACE_X} y="26">
          50–125 Hz: the band that carries
        </text>
        <text className="silencer-figure-axis" x="64" y="26" textAnchor="end">
          Hz
        </text>

        {/* The silencer itself: loud to its left, damped to its right. */}
        <line
          x1={SILENCER_X}
          y1="40"
          x2={SILENCER_X}
          y2="348"
          stroke="var(--text-faint)"
          strokeWidth="1.5"
          strokeDasharray="5 5"
          opacity="0.75"
        />
        <text
          className="silencer-figure-axis"
          x={SILENCER_X}
          y="366"
          textAnchor="middle"
        >
          silencer
        </text>

        {/* 50–125 Hz bracket */}
        <path
          d={`M84 ${bracketTop} L76 ${bracketTop} L76 ${bracketBottom} L84 ${bracketBottom}`}
          fill="none"
          stroke="var(--accent)"
          strokeWidth="1.5"
          opacity="0.9"
        />

        {rows.map((r, i) => (
          <g key={r.label} transform={`translate(0 ${ROW_Y(i)})`}>
            <text
              className={`silencer-figure-axis${r.inBand ? ' silencer-figure-axis--accent' : ''}`}
              x="64"
              y="0"
              textAnchor="end"
              dominantBaseline="middle"
            >
              {r.label}
            </text>
            <path
              transform={`translate(${TRACE_X} 0)`}
              d={r.d}
              fill="none"
              stroke={r.inBand ? 'var(--accent)' : 'var(--vermilion-light)'}
              strokeWidth={r.inBand ? 2.6 : 2}
              strokeLinecap="round"
              opacity={r.inBand ? 0.95 : 0.5}
            />
          </g>
        ))}
    </svg>
    </figure>
  );
}

function Silencer() {
  return (
    <section className="section silencer" id="silencer">
      <div className="wrap silencer-inner">
        <Cutaway />

        <div className="silencer-copy">
          <p className="eyebrow">The silencer</p>
          <h2 className="h2 silencer-title">Sound stops. Air doesn&rsquo;t.</h2>
          {/* Opens by naming the clause it exists to make signable. The section
              used to introduce itself as a product ("an air-permeable acoustic
              silencer that mounts around..."), which read as a new topic
              arriving rather than as the answer to the two clauses the reader
              has just been shown. */}
          <p className="lead silencer-lead">
            Holding a tonality clause means stopping a tone in an opening that
            has to keep moving air, and staying on it when it moves. Nothing off
            the shelf reaches low enough to do the first or follows the tone to
            do the second, so we built the element ourselves: a proprietary
            low-frequency silencer, designed in-house, that finds the fan tone
            and tracks it.
          </p>

          <div className="silencer-attrs">
            {ATTRIBUTES.map((a) => (
              <div className="silencer-attr" key={a.n}>
                <span className="silencer-attr-n">{a.n}</span>
                <div className="silencer-attr-text">
                  <h3 className="silencer-attr-title">{a.title}</h3>
                  <p className="silencer-attr-body">{a.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/*
       * THE NOVELTY CLAIM, and it is deliberately made on MECHANISM AND
       * AUTHORSHIP rather than on primacy.
       *
       * What is assertable: the element is our own design, it is not a catalog
       * part, and it belongs to a different family from the two things a
       * consultant can currently specify. All checkable, none of it dependent
       * on a dB figure we have not published.
       *
       * What must NEVER appear here:
       *   - "patented" or "patent-pending". Nothing is filed. The Valeo files
       *     say file patents BEFORE sharing samples — that is intent, not
       *     status, and the difference is legally material.
       *   - "first", "only", "nobody else". There is a live vendor set in
       *     acoustic metamaterials (Merford, Metasonixx, Applied Metamaterials,
       *     MetAcoustic and others), and duct silencers are specifically called
       *     out as a high-readiness domain in the patent landscape. A primacy
       *     claim would be checked in one search and would cost us the rest of
       *     the page.
       *
       * The quarter-wavelength figure describes what CONVENTIONAL treatment
       * needs, not what ours achieves, so it stays on the right side of the
       * no-performance-numbers rule. Keep it that way: the moment this
       * paragraph says how far WE move a number, it needs a test report behind
       * it.
       *
       * The tracking argument is the sharper half of the family claim and it
       * is entirely checkable: a reactive element is narrow by nature, a fan
       * tone is narrower still, and the fan tone moves. So "compact but
       * narrow" is only a weakness if the element cannot follow the tone —
       * which is exactly what ours does, and what a fixed resonator on a size
       * chart cannot. The two-fans-beating example is problem-side physics
       * (two nominally identical fans a few rpm apart), not a measurement of
       * ours, and it stays generic — no site, no frequencies.
       */}
      <div className="wrap">
        <div className="silencer-novelty">
          <p className="eyebrow silencer-novelty-eyebrow">Why it is new</p>
          <p className="silencer-novelty-body">
            <strong className="silencer-novelty-lead">
              A specifying engineer has two families to choose from today.
            </strong>{' '}
            Dissipative silencers absorb sound in porous material and need
            roughly a quarter-wavelength of depth to work, which is over a
            metre at 63&nbsp;Hz and more than any plant has room for. Reactive
            ones use geometry to reflect sound back at the source: compact, but
            narrow, and tuned once. A fan tone does not hold still: it moves
            with fan speed, and two nominally identical fans a few rpm apart
            put out two tones that beat against each other, which is the
            pulsing hum neighbors describe. A fixed element treats one of them
            and is off-target the moment the plant changes load. The nearest
            catalog equivalent to what a chiller needs, the acoustic louver, is
            notoriously weak below 250&nbsp;Hz regardless. Ours is a third kind
            of element: resonant, non-fibrous, and tracking. It works this low
            in the depth actually available on a plant, sheds nothing into the
            airstream, and re-tunes itself to the tone as the fans move. Each
            cartridge tunes on its own, so one off-tune never takes the array
            with it. It is our own design, set per site rather than picked off
            a size chart.
          </p>
        </div>
      </div>
    </section>
  );
}

export default Silencer;
